import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class CheckboxGroup extends i18nMixin(NeptuneElement) {

    templateMarkup = `
        <fieldset>
            <legend></legend>
            <slot></slot>
            <div>
                <span></span>
                <ul></ul>
            </div>
        </fieldset>
    `;

    componentCssUrl = '/src/components/form/checkboxGroup/checkbox-group.css';

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        const culture = this.getAttribute('culture');
		if (culture) { i18n.setLocale(culture); }
        
        if (!this.hasAttribute('name')) {
            throw new Error('neptune-checkbox-group: "name" attribute is required.');
        }
        
        this.processInitialAttributesAfterRender(CheckboxGroup.observedAttributes);
        this.setupAccessibility();
        this.initializeCheckboxes();
        this.setupCheckboxChangeHandler();
        this.#applyTranslations();
    }

    async disconnectedCallback() {
        this.removeEventListener('neptune-checkbox-change', this.handleCheckboxChange);
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['name', 'label', 'disabled', 'required', 'autocomplete', 'orientation', 'columns', 'error', 'error-text', 'supporting-text', 'min-selections', 'max-selections', 'culture'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    /**
    * Applies translated content for the checkbox group component.
    * @returns {void}
    */
    #applyTranslations() {
        if (this.hasAttribute('error') && this.hasAttribute('required') && this.getCheckedValues().length === 0) {
            this.setAttribute('error-text', this.translate('CHECKBOX_GROUP.VALIDATION.REQUIRED'));
        }
        this.updateMessages();
    }

    /**
    * Handles locale changes.
    * @returns {void}
    */
    onLocaleChange() {
        this.#applyTranslations();
    }

    setupAccessibility() {
        const fieldset = this.shadowRoot.querySelector('fieldset');
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');

        if (errorList) {
            errorList.id = `neptune-checkbox-group-errors-${Math.random().toString(36).substr(2, 9)}`;
        }

        if (supportingText) {
            supportingText.id = `neptune-checkbox-group-support-${Math.random().toString(36).substr(2, 9)}`;
        }

        const describedBy = [errorList?.id, supportingText?.id].filter(Boolean).join(' ');
        if (describedBy && fieldset) {
            fieldset.setAttribute('aria-describedby', describedBy);
        }
    }

    setupCheckboxChangeHandler() {
        this.handleCheckboxChange = (e) => {
            e.stopPropagation();
            
            const maxSelections = this.getAttribute('max-selections');
            
            if (maxSelections) {
                const checkedCount = this.getCheckedValues().length;
                const maxAllowed = parseInt(maxSelections);
                
                if (e.detail.checked && checkedCount > maxAllowed) {
                    const checkbox = e.target;
                    checkbox.removeAttribute('checked');
                    const checkboxInput = checkbox.shadowRoot?.querySelector('input[type="checkbox"]');
                    if (checkboxInput) {
                        checkboxInput.checked = false;
                    }
                    
                    this.setAttribute('error', '');
                    this.setAttribute('error-text', this.translate('CHECKBOX_GROUP.VALIDATION.MAX_SELECTIONS', { count: maxAllowed }));
                    return;
                }
            }
            
            if (this.hasAttribute('error')) {
                this.clearValidation();
            }
        };
        
        this.addEventListener('neptune-checkbox-change', this.handleCheckboxChange);
    }

    processAttribute(name, oldValue, newValue) {
        switch (name) {
            case 'label': {
                const legend = this.shadowRoot.querySelector('legend');
                if (legend) {
                    legend.textContent = newValue || '';
                    legend.style.display = newValue ? 'block' : 'none';
                }
                break;
            }
            case 'name': {
                if (!newValue) {
                    throw new Error('neptune-checkbox-group: "name" attribute is required.');
                }
                this.updateCheckboxNames();
                break;
            }
            case 'disabled': {
                this.updateCheckboxDisabledState();
                break;
            }
            case 'required': {
                this.updateCheckboxRequiredState();
                break;
            }
            case 'autocomplete': {
                this.updateCheckboxAutocompleteState();
                break;
            }
            case 'orientation':
            {
                this.validateOrientation();
                break;
            }
            case 'columns': {
                this.validateOrientation();
                break;
            }
            case 'error': {
                const fieldset = this.shadowRoot.querySelector('fieldset');
                if (fieldset) {
                    this.hasAttribute('error')
                        ? fieldset.setAttribute('aria-invalid', 'true')
                        : fieldset.removeAttribute('aria-invalid');
                }
                this.updateMessages();
                break;
            }
            case 'error-text':
            case 'supporting-text': {
                this.updateMessages();
                break;
            }
            case 'culture': {
                if (newValue) {
                    i18n.setLocale(newValue);
                    this.#applyTranslations();
                }
                break;
            }
            default: {
                break;
            }
        }
    }

    updateMessages() {
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');

        if (!errorList || !supportingText) {return;}

        errorList.setAttribute('aria-live', 'polite');
        supportingText.setAttribute('aria-live', 'polite');

        if (this.hasAttribute('error')) {
            const errorText = this.getAttribute('error-text') || '';
            const errors = errorText.split('|').filter(e => e.trim());

            errorList.innerHTML = '';
            if (errors.length > 0) {
                errors.forEach(error => {
                    const li = document.createElement('li');
                    li.textContent = error.trim();
                    errorList.appendChild(li);
                });
                errorList.style.display = 'block';
            } else {
                errorList.style.display = 'none';
            }

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            if (supportingTextValue) {
                supportingText.textContent = supportingTextValue;
                supportingText.classList.add('visually-hidden-a11y');
                supportingText.style.display = 'block';
            } else {
                supportingText.style.display = 'none';
            }
        } else {
            errorList.innerHTML = '';
            errorList.style.display = 'none';

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            supportingText.textContent = supportingTextValue;
            supportingText.classList.remove('visually-hidden-a11y');
            supportingText.style.display = supportingTextValue ? 'block' : 'none';
        }
    }

    validate() {
        const errors = [];
        const checkedCount = this.getCheckedValues().length;
        const minSelections = this.getAttribute('min-selections');
        const maxSelections = this.getAttribute('max-selections');
        const minAllowed = minSelections ? parseInt(minSelections, 10) : null;
        const maxAllowed = maxSelections ? parseInt(maxSelections, 10) : null;
        
        if (this.hasAttribute('required') && checkedCount === 0) {
            errors.push(this.translate('CHECKBOX_GROUP.VALIDATION.REQUIRED'));
        }

        if (minAllowed !== null && checkedCount < minAllowed) {
            errors.push(this.translate('CHECKBOX_GROUP.VALIDATION.MIN_SELECTIONS', { count: minAllowed }));
        }

        if (maxAllowed !== null && checkedCount > maxAllowed) {
            errors.push(this.translate('CHECKBOX_GROUP.VALIDATION.MAX_SELECTIONS', { count: maxAllowed }));
        }

        if (errors.length > 0) {
            this.setAttribute('error', '');
            this.setAttribute('error-text', errors.join('|'));
            return false;
        } 
            this.removeAttribute('error');
            this.removeAttribute('error-text');
            return true;
        
    }

    clearValidation() {
        this.removeAttribute('error');
        this.removeAttribute('error-text');
    }

    getValues() {
        return this.getCheckedValues();
    }

    isValid() {
        const checkedCount = this.getCheckedValues().length;
        const minSelections = this.getAttribute('min-selections');
        const maxSelections = this.getAttribute('max-selections');

        if (this.hasAttribute('required') && checkedCount === 0) {
            return false;
        }

        if (minSelections && checkedCount < parseInt(minSelections)) {
            return false;
        }

        if (maxSelections && checkedCount > parseInt(maxSelections)) {
            return false;
        }

        return true;
    }

    validateOrientation() {
        const fieldset = this.shadowRoot.querySelector('fieldset');
        if (!fieldset) {return;}

        const orientation = this.getAttribute('orientation');
        const columns = this.getAttribute('columns');

        if (orientation) {
            if (orientation === 'horizontal') {
                if (!columns) {
                    throw new Error('neptune-checkbox-group: "columns" attribute is required when orientation is "horizontal".');
                }
                fieldset.style.setProperty('--checkbox-group-columns', columns);
            } else if (orientation === 'vertical') {
                fieldset.style.removeProperty('--checkbox-group-columns');
            } else {
                throw new Error(`neptune-checkbox-group: Invalid orientation value "${orientation}". Expected "horizontal" or "vertical".`);
            }
        }
    }

    initializeCheckboxes() {
        const name = this.getAttribute('name');
        
        if (name) {
            this.updateCheckboxNames();
        }

        this.updateCheckboxRequiredState();
        this.updateCheckboxAutocompleteState();
        this.validateOrientation();
        this.updateMessages();
    }

    updateCheckboxNames() {
        const name = this.getAttribute('name');
        const checkboxes = this.querySelectorAll('neptune-checkbox');
        
        checkboxes.forEach(checkbox => {
            if (name) {
                checkbox.setAttribute('name', name);
            } else {
                checkbox.removeAttribute('name');
            }
        });
    }

    updateCheckboxDisabledState() {
        const isDisabled = this.hasAttribute('disabled');
        const checkboxes = this.querySelectorAll('neptune-checkbox');
        
        checkboxes.forEach(checkbox => {
            if (isDisabled) {
                checkbox.setAttribute('disabled', '');
            } else {
                checkbox.removeAttribute('disabled');
            }
        });
    }

    updateCheckboxRequiredState() {
        const checkboxes = this.querySelectorAll('neptune-checkbox');
        
        checkboxes.forEach(checkbox => {
            checkbox.removeAttribute('required');
        });
    }

    updateCheckboxAutocompleteState() {
        const autocomplete = this.getAttribute('autocomplete');
        const checkboxes = this.querySelectorAll('neptune-checkbox');
        
        checkboxes.forEach(checkbox => {
            if (autocomplete) {
                checkbox.setAttribute('autocomplete', autocomplete);
            } else {
                checkbox.setAttribute('autocomplete', 'off');
            }
        });
    }

    getCheckedValues() {
        const checkboxes = this.querySelectorAll('neptune-checkbox[checked]');
        return Array.from(checkboxes).map(cb => cb.getAttribute('value'));
    }
}

customElements.define('neptune-checkbox-group', CheckboxGroup);

export default CheckboxGroup;